import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SelectNative } from "../../components/ui/select-native";
import {
  createAdminAssetType,
  createAdminLiabilityType,
  deleteAdminAssetType,
  deleteAdminLiabilityType,
  listAdminAssetTypes,
  listAdminLiabilityTypes,
  updateAdminAssetType,
  updateAdminLiabilityType,
} from "../../lib/adminApi";
import { normalizeDetail } from "../../lib/errors";
import { AdminBudgetCategoriesPage } from "./AdminBudgetCategoriesPage";


const ASSET_EMPTY = {
  class: "financial",
  category: "",
  item_type: "",
  behavior: "appreciating",
  liquidity: "medium",
  income_generating: false,
  active: true,
  sort_order: 0,
};

const LIAB_EMPTY = { label: "", description: "", active: true, sort_order: 0 };

/** Admin taxonomy manager — asset types and liability types on one page (two tabs). */
export function AdminTaxonomyPage() {
  const [tab, setTab] = useState("assets");
  return (
    <div className="flex flex-col gap-6" data-testid="admin-taxonomy-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Master</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Asset & Liability Taxonomy</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted">
          The categories, item types, appreciation/depreciation rates and liquidity flags that
          power the user-facing Net Worth module. Edits go live for all members immediately.
        </p>
      </div>

      <div className="flex gap-2 border-b border-border">
        {[
          { k: "assets", label: "Asset types" },
          { k: "liabilities", label: "Liability types" },
          { k: "budget", label: "Budget categories" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`tax-tab-${t.k}`}
            className={`border-b-2 px-4 py-2 text-sm ${
              tab === t.k ? "border-gold text-gold" : "border-transparent text-muted hover:text-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "assets" && <AssetTypesTable />}
      {tab === "liabilities" && <LiabilityTypesTable />}
      {tab === "budget" && <AdminBudgetCategoriesPage />}
    </div>
  );
}

/* -------------------------------- Asset Types -------------------------------- */

function AssetTypesTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, row?}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listAdminAssetTypes());
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't load asset types."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(row) {
    if (!window.confirm(`Delete "${row.item_type}"? This can't be undone.`)) return;
    try {
      await deleteAdminAssetType(row.id);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete."));
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm text-muted">{rows.length} asset types</span>
        <Button size="sm" onClick={() => setDialog({ mode: "create" })} data-testid="asset-type-add-btn" className="gap-2">
          <Plus className="h-3.5 w-3.5" /> Add type
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
            <tr>
              <th className="px-3 py-3">Class</th>
              <th className="px-3 py-3">Category</th>
              <th className="px-3 py-3">Item</th>
              <th className="px-3 py-3">Behavior</th>
              <th className="px-3 py-3">Liquidity</th>
              <th className="px-3 py-3">Income?</th>
              <th className="px-3 py-3">Active</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted">Loading…</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-border/70 last:border-b-0 hover:bg-elevated/60" data-testid={`asset-row-${r.id}`}>
                <td className="px-3 py-2 text-xs uppercase text-muted">{r.class}</td>
                <td className="px-3 py-2 text-text">{r.category}</td>
                <td className="px-3 py-2 text-text">{r.item_type}</td>
                <td className={`px-3 py-2 text-xs ${r.behavior === "depreciating" ? "text-red-400" : r.behavior === "stable" ? "text-muted" : "text-emerald-400"}`}>
                  {r.behavior}
                </td>
                <td className="px-3 py-2 text-xs uppercase text-muted">{r.liquidity}</td>
                <td className="px-3 py-2 text-xs">{r.income_generating ? "Yes" : "—"}</td>
                <td className="px-3 py-2 text-xs">{r.active ? "Yes" : <span className="text-red-400">No</span>}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "edit", row: r })} data-testid={`asset-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} data-testid={`asset-delete-${r.id}`}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && (
        <AssetTypeDialog
          initial={dialog.row}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSaved={async () => { setDialog(null); await load(); }}
        />
      )}
    </div>
  );
}

function AssetTypeDialog({ initial, mode, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...ASSET_EMPTY, ...initial } : ASSET_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  function set(field) {
    return (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked
        : e.target.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value))
        : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        class: form.class,
        category: form.category.trim(),
        item_type: form.item_type.trim(),
        behavior: form.behavior,
        liquidity: form.liquidity,
        income_generating: !!form.income_generating,
        active: !!form.active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (mode === "edit" && initial) {
        await updateAdminAssetType(initial.id, payload);
      } else {
        await createAdminAssetType(payload);
      }
      toast.success("Saved.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="asset-type-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <h3 className="text-lg font-semibold text-text">{mode === "edit" ? "Edit asset type" : "Add asset type"}</h3>
          <button onClick={onClose} className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Class</Label>
            <SelectNative value={form.class} onChange={set("class")}>
              <option value="financial">Financial</option>
              <option value="physical">Physical</option>
            </SelectNative>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Behavior</Label>
            <SelectNative value={form.behavior} onChange={set("behavior")}>
              <option value="appreciating">Appreciating</option>
              <option value="depreciating">Depreciating</option>
              <option value="stable">Stable</option>
            </SelectNative>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Category *</Label>
            <Input value={form.category} onChange={set("category")} placeholder="Investments, Real Estate…" required data-testid="asset-form-category" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Item type *</Label>
            <Input value={form.item_type} onChange={set("item_type")} placeholder="Equity, Gold, Car…" required data-testid="asset-form-item" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Liquidity</Label>
            <SelectNative value={form.liquidity} onChange={set("liquidity")}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </SelectNative>
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={form.income_generating} onChange={set("income_generating")} />
              Income-generating (rental, dividend etc.)
            </label>
            <label className="flex items-center gap-2 text-sm text-text ml-6">
              <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="asset-form-active" />
              Active (users can pick it)
            </label>
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="asset-form-submit">{submitting ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------ Liability Types ------------------------------ */

function LiabilityTypesTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listAdminLiabilityTypes());
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't load liability types."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(row) {
    if (!window.confirm(`Delete "${row.label}"?`)) return;
    try {
      await deleteAdminLiabilityType(row.id);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete."));
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm text-muted">{rows.length} liability types</span>
        <Button size="sm" onClick={() => setDialog({ mode: "create" })} data-testid="liab-type-add-btn" className="gap-2">
          <Plus className="h-3.5 w-3.5" /> Add type
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
            <tr>
              <th className="px-3 py-3">Label</th>
              <th className="px-3 py-3">Description</th>
              <th className="px-3 py-3">Active</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted">Loading…</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-border/70 last:border-b-0 hover:bg-elevated/60" data-testid={`liab-row-${r.id}`}>
                <td className="px-3 py-2 text-text">{r.label}</td>
                <td className="px-3 py-2 text-xs text-muted">{r.description || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.active ? "Yes" : <span className="text-red-400">No</span>}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "edit", row: r })} data-testid={`liab-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} data-testid={`liab-delete-${r.id}`}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && (
        <LiabilityTypeDialog
          initial={dialog.row}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSaved={async () => { setDialog(null); await load(); }}
        />
      )}
    </div>
  );
}

function LiabilityTypeDialog({ initial, mode, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...LIAB_EMPTY, ...initial } : LIAB_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  function set(field) {
    return (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked
        : e.target.type === "number" ? Number(e.target.value)
        : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        label: form.label.trim(),
        description: form.description?.trim() || null,
        active: !!form.active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (mode === "edit" && initial) {
        await updateAdminLiabilityType(initial.id, payload);
      } else {
        await createAdminLiabilityType(payload);
      }
      toast.success("Saved.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="liab-type-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-lg font-semibold text-text">{mode === "edit" ? "Edit liability type" : "Add liability type"}</h3>
          <button onClick={onClose} className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1.5">
            <Label>Label *</Label>
            <Input value={form.label} onChange={set("label")} required data-testid="liab-form-label" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Description</Label>
            <Input value={form.description || ""} onChange={set("description")} placeholder="Optional short blurb" />
          </div>
          <label className="flex items-center gap-2 text-sm text-text">
            <input type="checkbox" checked={form.active} onChange={set("active")} />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="liab-form-submit">{submitting ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminTaxonomyPage;
